import { forwardRef, useEffect, useMemo, useRef } from 'react'
import * as d3 from 'd3'
import dayjs from 'dayjs'
import theme from 'theme'
import { useThemeColors } from 'hooks/useThemeColors'
import { useAppSelector } from 'store/hooks'
import { selectThemeMode } from 'store/theme/themeSlice'

/* Utils */
import { HistoryDataPoint } from './TrendView'
import { CPITimePeriod } from '../../../../../models'
import { fillBlankData, findNearestXIndex } from '../../../library/utils'
import {
  filterHistoricalData,
  findHistoricalUniqueMonths,
  getCategoryPathAndPositions,
  groupHistoricalDataByWeeks
} from '../../../category-dashboard/sections/historical-data/performanceScoreUtils'

// const chartWidthStart = 30
const chartWidthStart = 0
const chartHeightStart = 16

interface Props {
  margin: { top: number; right: number; bottom: number; left: number }
  width: number
  height: number
  data: HistoryDataPoint[]
}

const OverallPerformanceChart = forwardRef<any, Props>(({ data, width, height, margin }, ref) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { containerRef, svgRef } = ref
  const numberOfStripes = 4
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const barHeight = height + margin.top + margin.bottom
  const getColor = useThemeColors()
  const themeMode = useAppSelector(selectThemeMode)

  const isOverPerform = useMemo(() => {
    const findItem = data.find((item) => item.yValue > 100)
    return !!findItem
  }, [data])

  const buildChart = (data: HistoryDataPoint[], timeRange: number) => {
    const filteredData = filterHistoricalData(timeRange, data)
    const tooltip = d3.select(tooltipRef.current)
    const svg = d3.select(svgRef.current)

    /*
     *  Populate each stripe with data points for that month
     * */
    const yAxisDomain = isOverPerform ? [0, 150] : [0, 100]
    const uniqueMonths = findHistoricalUniqueMonths(filteredData, CPITimePeriod.Month, data)
    const stripeWidth = width / uniqueMonths.length

    const [pathStr, positions, pathStartPoint] = getCategoryPathAndPositions({
      uniqueMonths,
      stripeWidth,
      height: barHeight,
      filteredData,
      yAxisDomain,
      chartHeightStart,
      chartWidthStart
    })

    const xScaleGroup = d3.scaleBand().domain(uniqueMonths).range([chartWidthStart, width]).padding(0)
    const yScale = d3
      .scaleLinear()
      .domain(yAxisDomain)
      .range([barHeight - chartHeightStart / 2, chartHeightStart / 2])
      .nice()

    /*
     * Cartesian Grid Bars
     * */
    const existingBars = svg.selectAll('.bar')
    if (!existingBars.empty()) existingBars.remove()
    svg
      .selectAll('.bar')
      .data(uniqueMonths)
      .enter()
      .append('rect')
      .attr('fill', 'transparent')
      .attr('class', 'bar')
      .attr('x', (d) => xScaleGroup(d) || 0)
      .attr('y', 0)
      .attr('width', xScaleGroup.bandwidth())
      .attr('height', barHeight)
      .on('mouseout', () => {
        tooltip.style('display', 'none')
        const circleInner = svg.select('.hover-circle-inner')
        circleInner.attr('display', 'none')
        const circleOuter = svg.select('.hover-circle-outer')
        circleOuter.attr('display', 'none')
      })

    /*
     * Mouse Move for data point tooltip
     * */
    svg.on('mousemove', (event: any) => {
      if (!data.length) return
      const [mouseX, mouseY] = d3.pointer(event)
      const dataPoint = findNearestXIndex(positions, mouseX)
      if (mouseX > width + chartWidthStart || mouseY > height + chartHeightStart) return null
      const matchingDataPoint = filteredData[dataPoint?.index || 0]
      if (dataPoint) mouseMove(event, dataPoint, matchingDataPoint)
    })

    /*
     * Hover Data Point Circle
     * */
    const appendHoverCircles = () => {
      const hoverCircleInner = svg.select('.hover-circle-inner')
      if (!hoverCircleInner.empty()) hoverCircleInner.remove()
      svg
        .append('circle')
        .attr('class', 'hover-circle-inner')
        .attr('cx', 100)
        .attr('cy', 200)
        .attr('r', 5.5)
        .attr('fill', theme.colors.white)
        .attr('stroke', '#000')
        .attr('stroke-width', 2)
        .attr('stroke-opacity', 1)
        .attr('display', 'none')
      const hoverCircleOuter = svg.select('.hover-circle-outer')
      if (!hoverCircleOuter.empty()) hoverCircleOuter.remove()
      svg
        .append('circle')
        .attr('class', 'hover-circle-outer')
        .attr('cx', 100)
        .attr('cy', 200)
        .attr('r', 5)
        .attr('fill', 'none')
        .attr('stroke', theme.colors.white)
        .attr('stroke-width', 15)
        .attr('stroke-opacity', 0.5)
        .attr('display', 'none')
    }
    appendHoverCircles()

    /*
     * Hover Tooltip
     * */
    const mouseMove = (event: any, dataPoint: any, d: any) => {
      tooltip.style('display', 'block')
      const circleInner = svg.select('.hover-circle-inner')
      const circleOuter = svg.select('.hover-circle-outer')
      circleInner.attr('cx', dataPoint.x)
      circleInner.attr('cy', dataPoint.y)
      circleInner.attr('display', 'block')
      circleOuter.attr('cx', dataPoint.x)
      circleOuter.attr('cy', dataPoint.y)
      circleOuter.attr('display', 'block')
      cursorLine.attr('x1', dataPoint.x).attr('x2', dataPoint.x).style('display', 'block')

      const value = d.dummy ? '' : `${d.yValue}%`
      const unitInfoText = d.dummy ? `There is no data for this date` : `Performance score: ${value}`
      const startDate = dayjs(d.start)
      const endDate = dayjs(d.end)
      const dateRangeLabel =
        startDate.month === endDate.month
          ? `${startDate.format('MMM D')} - ${endDate.format('D, YYYY')}`
          : `${startDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`

      tooltip
        .html(
          `
          <p class='tooltip-date'>${dateRangeLabel}</p>
          <p class='tooltip-units'>${unitInfoText}</p>
          `
        )
        .style('left', dataPoint.x + 5 + 'px')
        .style('top', dataPoint.y + 10 + 'px')
    }

    /*
     * Y-Axis Tick lines
     * */
    const existingHorizontalLines = svg.selectAll('.horizontal-grid-line')
    if (!existingHorizontalLines.empty()) existingHorizontalLines.remove()

    const tickValues = yScale.ticks(3)

    svg
      .selectAll('.horizontal-grid-line')
      .data(tickValues)
      .enter()
      .append('line')
      .attr('class', 'horizontal-grid-line')
      .attr('x1', 32)
      .attr('x2', width)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#6C5B67')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '8 8')
      .attr('opacity', 0.5)
      .attr('stroke-linecap', 'round')

    /*
     * Cursor Line
     * */
    const existingline = svg.selectAll('.cursor-line')
    if (!existingline.empty()) existingline.remove()
    const cursorLine = svg
      .append('line')
      .attr('class', 'cursor-line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', height + margin.top + margin.bottom)
      .attr('stroke', getColor('text-primary'))
      .attr('stroke-width', 1)
      .style('display', 'none')
    svg.on('mouseout', () => {
      cursorLine.style('display', 'none')
      tooltip.style('display', 'none')
      const circleInner = svg.select('.hover-circle-inner')
      circleInner.attr('display', 'none')
      const circleOuter = svg.select('.hover-circle-outer')
      circleOuter.attr('display', 'none')
    })

    const filledArea = svg.select('.filled-area')
    const filledAreaStroke = svg.select('.filled-area-stroke')

    /*
     *  Filled Area
     * */
    const appendFilledArea = () => {
      const existingFilledArea = svg.selectAll('.filled-area')
      if (!existingFilledArea.empty()) existingFilledArea.remove()
      svg
        .append('path')
        .datum(filteredData)
        .attr(
          'd',
          `${pathStr}` + `L${positions[positions.length - 1].x} ${barHeight} L ${pathStartPoint.x} ${barHeight} Z`
        )
        .attr('class', 'filled-area')
        .attr('fill', themeMode === 'light' ? 'rgba(161, 59, 127, 0.16)' : theme.colors.primaryLight)
        .attr('stroke', 'none')
        .on('mouseout', () => {
          tooltip.style('display', 'none')
          const circleInner = svg.select('.hover-circle-inner')
          circleInner.attr('display', 'none')
          const circleOuter = svg.select('.hover-circle-outer')
          circleOuter.attr('display', 'none')
        })
    }
    appendFilledArea()

    /*
     * Filled Area Stroke
     * */
    const appendFilledAreaStroke = () => {
      const existingFilledAreaStroke = svg.selectAll('.filled-area-stroke')
      if (!existingFilledAreaStroke.empty()) existingFilledAreaStroke.remove()
      svg
        .append('path')
        .attr('fill', 'none')
        .attr('stroke', getColor('cta'))
        .attr('d', `${pathStr}`)
        .attr('class', 'filled-area-stroke')
        .attr('stroke-width', 2)
        .attr('stroke-linejoin', 'bevel')
    }
    appendFilledAreaStroke()

    if (!filledArea.empty()) filledArea.remove()
    if (!filledAreaStroke.empty()) filledAreaStroke.remove()

    /*
     *  Y-Axis
     * */
    const yAxis = isOverPerform
      ? d3
          .axisRight(yScale)
          .ticks(3)
          .tickFormat((d) => {
            return `${d}%`
          })
      : d3
          .axisRight(yScale)
          .ticks(3)
          .tickFormat((d) => `${d}%`)
    const existingYAxis = svg.selectAll('.y-axis')
    if (!existingYAxis.empty()) existingYAxis.remove()
    const yAxisGroup = svg
      .append('g')
      .attr('class', 'y-axis')
      .attr('color', 'none')
      .attr('transform', 'translate(-16, 0)')
      .call(yAxis)
    yAxisGroup
      .selectAll('text')
      .attr('color', getColor('calendar-day'))
      .attr('text-align', 'right')
      .attr('font-size', '14px')
      .attr('font-weight', theme.typography.fontWeight.bold)
      .attr('letter-spacing', '0.1px')
      .attr('line-height', '22px')
      .attr('font-family', theme.typography.fontFamily.primary)
    yAxisGroup.selectAll('path').attr('display', 'none')
    yAxisGroup.selectAll('line').attr('display', 'none')

    svg.selectAll('.filled-area').raise()
    svg.selectAll('.filled-area-stroke').raise()
    svg.selectAll('.hover-circle-outer').raise()
    svg.selectAll('.hover-circle-inner').raise()
    svg.selectAll('.cursor-line').raise()
    svg.selectAll('.horizontal-line-middle').raise()
  }

  useEffect(() => {
    if (!containerRef.current || !data.length) return

    const groupedData = groupHistoricalDataByWeeks(data)
    const filledData = fillBlankData(groupedData, CPITimePeriod.Month) as any[]

    buildChart(filledData, numberOfStripes)
  }, [data, width, tooltipRef])

  return (
    <>
      <div ref={tooltipRef} className="overall-performance-chart-tooltip" />
      <svg
        ref={svgRef}
        id="overall-performance-chart-svg"
        className="overall-performance-chart-svg"
        width={width}
        height={barHeight}
      />
    </>
  )
})

export default OverallPerformanceChart
